import React, { useCallback, useMemo, useState } from 'react'
import { map } from 'ramda'
import { AggregateTable, Button, Header, Loader, Select } from '../../../../views/components'
import styles from './AgenciesTable.module.scss'
import SettingIcon from 'components/atoms/Icons/Controls/Settings'
import { subscribe } from 'react-contextual'
import { SearchFilterProvider } from '../../../../views/providers'
import AgenciesProvider from '../../AgenciesProvider'
import { useSelector } from 'react-redux'
import { apiStateSelectors } from '../../../api/apiState'
import { agencyApiEvents } from '../../../api/agency'
import { stringUtils } from '../../../../utils'
import ConfirmationDeactivationModal from '../ConfirmationDeactivationModal/ConfirmationDeactivationModal'
import ConfirmationActivationModal from '../ConfirmationActivationModal/ConfirmationActivationModal'
import GroupRoyaltiesModal from '../GroupRoyaltiesModal/GroupRoyaltiesModal'

const confirmationStateTypes = {
  activate: 'activate',
  deactivate: 'deactivate',
  groupRoyalties: 'groupRoyalties',
} as const

type ConfirmationType = typeof confirmationStateTypes[keyof typeof confirmationStateTypes]

interface Agency {
  agencyId: string
  agencyName: string
  tradingAs?: string
  emailAddress?: string
  isActive?: boolean
  agencyGroupId?: string
  agencyGroupName?: string
}

interface RoyaltyOption {
  label: string
  value: string
}

interface TableColumn {
  Header: string
  accessor: string
  style?: React.CSSProperties
}

interface ConfirmationState {
  type: ConfirmationType | ''
  isOpen: boolean
  agencyName: string
  agencyId: string
}

interface GroupRoyaltiesState {
  groupRoyaltiesSelection: RoyaltyOption
  isOpen: boolean
  agencyId: string
  agencyName: string
}

interface AgenciesTableProps {
  searchQuery: string
  agenciesList: Agency[]
}

const AgenciesTable = ({ searchQuery, agenciesList }: AgenciesTableProps): React.ReactElement => {
  const agencyId = {
    Header: 'Agency ID',
    accessor: 'agencyId',
    style: {
      maxWidth: '700px',
    },
  }

  const companyName = {
    Header: 'Agency Name',
    accessor: 'agencyName',
    style: {
      maxWidth: '250px',
    },
  }

  const tradingAs = {
    Header: 'Trading As',
    accessor: 'tradingAs',
    style: {
      maxWidth: '250px',
    },
  }

  const emailAddress = {
    Header: 'Email Address',
    accessor: 'emailAddress',
    style: {
      maxWidth: '250px',
    },
  }

  const groupRoyaltiesColumn = {
    Header: 'Group Royalties',
    accessor: 'groupRoyalties',
    style: {
      maxWidth: '150px',
      justifyContent: 'flex-end',
    },
  }

  const actionColumn = {
    Header: 'Activation',
    accessor: 'action',
    style: {
      maxWidth: '150px',
      justifyContent: 'flex-end',
    },
  }

  // Previous const columns = [{}]
  const columns: TableColumn[] = [agencyId, companyName, tradingAs, emailAddress, groupRoyaltiesColumn, actionColumn]

  const intialConfirmationState: ConfirmationState = {
    type: '',
    isOpen: false,
    agencyName: '',
    agencyId: '',
  }

  const initialGroupRoyaltyConfirmationState: GroupRoyaltiesState = {
    groupRoyaltiesSelection: { label: 'None', value: 'none' },
    isOpen: false,
    agencyId: '',
    agencyName: '',
  }

  const [confirmationState, setConfirmationState] = useState<ConfirmationState>(intialConfirmationState)

  const [groupRoyaltiesState, setGroupRoyaltiesState] = useState<GroupRoyaltiesState>(
    initialGroupRoyaltyConfirmationState,
  )

  const isLoading = useSelector(state => apiStateSelectors.isLoading(state)([agencyApiEvents.agencyListing_request]))

  const handleDeactivate = useCallback((agencyId: string, agencyName: string) => {
    setConfirmationState({
      type: confirmationStateTypes.deactivate,
      isOpen: true,
      agencyName,
      agencyId,
    })
  }, [])

  const handleActivate = useCallback((agencyId: string, agencyName: string) => {
    setConfirmationState({
      type: confirmationStateTypes.activate,
      isOpen: true,
      agencyName,
      agencyId,
    })
  }, [])

  const handleGroupRoyaltiesSelection = useCallback(
    (agencyId: string, agencyName: string, selection: RoyaltyOption) => {
      setGroupRoyaltiesState({
        groupRoyaltiesSelection: { label: selection.label, value: selection.value },
        isOpen: true,
        agencyId,
        agencyName,
      })
    },
    [],
  )

  const buildRow = (agency: Agency) => {
    return {
      ...agency,
      groupRoyalties: agency?.isActive && (
        <Select
          key={agency.agencyId}
          className={styles.groupRoyalties}
          name="groupRoyalties"
          value={
            agency.agencyGroupId
              ? { label: agency.agencyGroupName, value: agency.agencyGroupId }
              : initialGroupRoyaltyConfirmationState.groupRoyaltiesSelection
          }
          options={[
            { label: 'None', value: 'none' },
            { label: 'RRF W/CAPE (PTY) LTD', value: 'rawson' },
          ]}
          onChange={option =>
            option &&
            handleGroupRoyaltiesSelection(agency.agencyId, agency.agencyName, {
              label: option.label ?? '',
              value: option.value,
            })
          }
        />
      ),
      action: agency?.isActive ? (
        <Button
          onClick={() => handleDeactivate(agency.agencyId, agency.agencyName)}
          className={styles.deactivate}
          size="sm"
        >
          Deactivate
        </Button>
      ) : (
        <Button
          onClick={() => handleActivate(agency.agencyId, agency.agencyName)}
          className={styles.activate}
          size="sm"
        >
          Activate
        </Button>
      ),
    }
  }

  const data = agenciesList

  const filteredData = useMemo(() => {
    return data.filter((agency: Agency) => stringUtils.jsonStringSearch(searchQuery, agency))
  }, [searchQuery, data])

  const tableData = searchQuery ? map(buildRow, filteredData) : map(buildRow, data)

  return (
    <>
      <div className={styles.root}>
        <Header icon={<SettingIcon />} text="Agencies" />
        {isLoading ? (
          <Loader />
        ) : (
          <AggregateTable rowHeight={80} columns={columns} data={tableData} interactive={false} />
        )}
      </div>
      <ConfirmationDeactivationModal
        isOpen={confirmationState.isOpen && confirmationState.type === confirmationStateTypes.deactivate}
        onClose={() => setConfirmationState(intialConfirmationState)}
        agencyName={confirmationState.agencyName}
        agencyId={confirmationState.agencyId}
      />
      <ConfirmationActivationModal
        isOpen={confirmationState.isOpen && confirmationState.type === confirmationStateTypes.activate}
        onClose={() => setConfirmationState(intialConfirmationState)}
        agencyName={confirmationState.agencyName}
        agencyId={confirmationState.agencyId}
      />
      <GroupRoyaltiesModal
        isOpen={groupRoyaltiesState.isOpen}
        onClose={() => setGroupRoyaltiesState(initialGroupRoyaltyConfirmationState)}
        agencyGroupName={groupRoyaltiesState.groupRoyaltiesSelection.label}
        agencyGroupValue={groupRoyaltiesState.groupRoyaltiesSelection.value}
        agencyId={groupRoyaltiesState.agencyId}
        agencyName={groupRoyaltiesState.agencyName}
      />
    </>
  )
}

export default subscribe(
  [SearchFilterProvider, AgenciesProvider],
  ({ searchQuery }: { searchQuery: string }, { agenciesList }: { agenciesList: Agency[] }) => ({
    searchQuery,
    agenciesList,
  }),
)(AgenciesTable)
